import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../config/axios";

const useIncomingMessageNotice = ({ userId, shopId, profileId }) => {
  const [receivedMessageCount, setReceivedMessageCount] = useState(0);
  const [newMessageVersion, setNewMessageVersion] = useState(0);
  const seenMessagesRef = useRef(new Set());
  const profileStreamActiveRef = useRef(false);
  const recipientProfileId = profileId || shopId;

  useEffect(() => {
    setReceivedMessageCount(0);
    seenMessagesRef.current.clear();
    profileStreamActiveRef.current = false;
    if (!userId || !recipientProfileId) return undefined;

    const profileIds = new Set(
      [userId, recipientProfileId].filter(Boolean).map(String),
    );
    const socket = io(SOCKET_URL);
    const apiBase = (
      process.env.REACT_APP_API_URL || "http://localhost:8080/api"
    ).replace(/\/$/, "");
    const eventSource = new EventSource(
      `${apiBase}/message/unread-stream?userId=${encodeURIComponent(recipientProfileId)}`,
    );
    const joinProfileRooms = () => {
      profileIds.forEach((profileId) => socket.emit("join_user", profileId));
    };
    eventSource.addEventListener("connected", () => {
      profileStreamActiveRef.current = true;
    });
    eventSource.addEventListener("profile_unread_count", (event) => {
      try {
        const { count, isNewMessage } = JSON.parse(event.data);
        profileStreamActiveRef.current = true;
        setReceivedMessageCount(Number(count) || 0);
        if (isNewMessage) {
          setNewMessageVersion((version) => version + 1);
        }
      } catch (error) {
        console.warn("[SSE] Invalid profile unread count.", error);
      }
    });
    eventSource.onerror = () => {
      profileStreamActiveRef.current = false;
    };

    const handleReceiveMessage = (message) => {
      if (profileStreamActiveRef.current) return;
      const receiverId = String(message?.receiverId || "");
      const senderId = String(
        message?.sender || message?.sendershopid || message?.senderId || "",
      );
      if (
        receiverId !== String(recipientProfileId) ||
        profileIds.has(senderId)
      ) {
        return;
      }

      const messageKey = String(
        message?._id ||
          [
            message?.chatId,
            receiverId,
            senderId,
            message?.timestamp || message?.createdAt || message?.content,
          ].join(":"),
      );
      if (seenMessagesRef.current.has(messageKey)) return;
      if (seenMessagesRef.current.size >= 200) {
        seenMessagesRef.current.clear();
      }
      seenMessagesRef.current.add(messageKey);
      setReceivedMessageCount((count) => count + 1);
      setNewMessageVersion((version) => version + 1);
    };

    const handleMessagesRead = () => {
      setReceivedMessageCount(0);
      seenMessagesRef.current.clear();
    };

    socket.on("connect", joinProfileRooms);
    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("shop_message_received", handleReceiveMessage);
    socket.on("messages_read", handleMessagesRead);
    if (socket.connected) joinProfileRooms();

    const handleWindowMessagesRead = () => {
      setReceivedMessageCount(0);
      seenMessagesRef.current.clear();
    };
    window.addEventListener("messages_read", handleWindowMessagesRead);
    window.addEventListener("selectezi:messages_read", handleWindowMessagesRead);

    return () => {
      window.removeEventListener("messages_read", handleWindowMessagesRead);
      window.removeEventListener("selectezi:messages_read", handleWindowMessagesRead);
      socket.off("connect", joinProfileRooms);
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("shop_message_received", handleReceiveMessage);
      socket.off("messages_read", handleMessagesRead);
      socket.disconnect();
      eventSource.close();
    };
  }, [recipientProfileId, userId]);

  return {
    receivedMessageCount,
    newMessageVersion,
    clearReceivedMessageCount: () => setReceivedMessageCount(0),
  };
};

export default useIncomingMessageNotice;